import {
  VdPerformanceTracking,
  VdTracking,
  VdFeatureManager,
} from '@vend/vend.js'
import angular from 'angular'
import { ApiRegister } from '@vend/types'
import { getFID, Metric } from 'web-vitals'

import { getNetworkInfo, trackError } from '@vend/utilities'
import { $q } from '../ng-compat/imports'
import { EventEmitter } from '../util/event-emitter'
import { WorkflowService } from '../components/workflows/workflow.service'
import { PosStateManager } from './pos-state-manager'
import { RegisterManager } from './register-manager'
import { SearchManager } from './search-manager'
import { SyncManager } from './sync-manager'
import { UserManager } from './user-manager'
import {
  fetchTranslations,
  determineFrenchCanadianSettingsForReceipts,
} from './web-register-tx-manager'
import { ClientApiManager } from './client-api-manager'

const REGISTER_SELECTION_MEASURE = 'register-selection'
const DATA_STORES_SYNCED_MEASURE = 'data-stores-synced'
const SEARCH_INDEXES_INIT_MEASURE = 'search-indexes-init'

class RegistersUnavailableError extends Error {
  constructor(...args: $TSFixMe[]) {
    super(...args)
    this.name = 'RegistersUnavailableError'
  }
}

interface RegisterSelectedEvent {
  selectedRegister: ApiRegister
}

interface RegisterSelectRequiredEvent {
  registersByOutlet: $TSFixMe
}

type Events = {
  registerSelected: [RegisterSelectedEvent]
  registerSelectRequired: [RegisterSelectRequiredEvent]
  registersUnavailable: []
  translationsAvailable: []
}

/**
 * Manages the intialisation flow and some of the overall state of the Web Register app. Works in tandem with the
 * WebRegisterCtrl controller.
 *
 * @class WebRegisterManager
 * @extends {EventEmitter}
 * @constructor
 */
export class WebRegisterManager extends EventEmitter<Events> {
  private _selectRegisterModalShown = false
  private _selectingRegister = false
  private _translationsFetched = false
  public initialisationComplete = false
  public isWarmBoot = false

  constructor(
    private posStateManager: PosStateManager,
    private searchManager: SearchManager,
    private syncManager: SyncManager,
    private userManager: UserManager,
    private registerManager: RegisterManager,
    private clientApiManager: ClientApiManager,
    private tracking: $TSFixMe,
    private vdTracking: VdTracking,
    private vdFeatureManager: VdFeatureManager,
    private vdPerformanceTracking: VdPerformanceTracking,
    private workflowService: WorkflowService
  ) {
    'ngInject'
    super()
    this.userManager.on('loggedInUserChange', () =>
      this._onLoggedInUserChange()
    )

    // Wait for the receipt settings store to be synced and a complete set of receipt template types
    // to be available before attempting to determine French Canadian requirements for receipts.
    this.syncManager.on('entitySyncComplete:receiptSettings', () =>
      this.syncManager
        .whenCompleteEntityDataAvailable('receiptTemplateTypes')
        .then(() => determineFrenchCanadianSettingsForReceipts())
    )
  }

  /**
   * Initialise the Web Register application - orchestrating data sync, search indexing and register selection.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once initialisation is complete (data sync and register selection are
   *         complete, search indexing is underway)
   */
  public init() {
    const disableTranslation = this.vdFeatureManager.get('disable_translation')
    const whenTranslationsFetched = fetchTranslations(disableTranslation)
    const whenSearchIndexesInitialised = this.searchManager.init()
    const whenInitialSyncComplete = this._performInitialSync()
    const whenRegisterSelected = this._waitForSelectRegister()

    whenTranslationsFetched.then(() => {
      this._translationsFetched = true
      this.emit('translationsAvailable')
    })

    // Track Register Selection
    let registerSelection: $TSFixMe
    this.vdPerformanceTracking.start(REGISTER_SELECTION_MEASURE)
    whenRegisterSelected.then(() => {
      registerSelection = this.vdPerformanceTracking.stop(
        REGISTER_SELECTION_MEASURE
      )
    })
    // Track Syncing of Data Stores
    let dataStoresSynced: $TSFixMe
    this.vdPerformanceTracking.start(DATA_STORES_SYNCED_MEASURE)
    whenInitialSyncComplete.then(() => {
      dataStoresSynced = this.vdPerformanceTracking.stop(
        DATA_STORES_SYNCED_MEASURE
      )
      // Track Initialisation of Search Indexes (Products and Customers)
      let searchIndexesInitialised: $TSFixMe
      this.vdPerformanceTracking.start(SEARCH_INDEXES_INIT_MEASURE)
      return whenSearchIndexesInitialised
        .then(() => {
          return this.searchManager.whenSyncComplete().then(() => {
            searchIndexesInitialised = this.vdPerformanceTracking.stop(
              SEARCH_INDEXES_INIT_MEASURE
            )
          })
        })
        .then(() => {
          const webRegisterAvailable = this.vdPerformanceTracking.measure({
            name: 'web-register-available',
          })
          // Track Web Register Metrics
          this.trackInitMetrics({
            dataStoresSynced,
            registerSelection,
            searchIndexesInitialised,
            webRegisterAvailable,
          })
          // Track Page Metrics
          this.vdPerformanceTracking.trackPageMetrics()
          // Track Device Metrics
          this.vdPerformanceTracking.trackDeviceMetrics()
          // Track FID Metric
          this.trackFIDMetric()
        })
    })
    this.workflowService.init()
    this.tracking.init()
    this.clientApiManager.init()
    return $q
      .all([
        whenTranslationsFetched,
        whenSearchIndexesInitialised,
        whenInitialSyncComplete,
        whenRegisterSelected,
      ])
      .then(() => {
        this.initialisationComplete = true
      })
  }

  public getTranslationsAvailable(): boolean {
    return this._translationsFetched
  }

  // ============================== Private API ============================== //
  /**
   * Tracks First Input Delay (FID) metric. The time from when a user attempts to interact with Web Register to the time
   * when its actually able to respond.
   */
  public trackFIDMetric() {
    const sendMetrics = ({ name, delta }: Metric) => {
      this.vdTracking.trackUserEvent({
        eventName: 'First Input Delay',
        metadata: {
          name,
          delta,
        },
      })
    }
    getFID(sendMetrics)
  }

  /**
   * Sends a UBE with loading / syncing metrics.
   *
   * @method trackInitMetrics
   *
   * @param {Object} trackingData
   *        @param {PerformanceMeasure} trackingData.dataStoresSynced
   *               Timings for the syncing of data stores.
   *
   *        @param {PerformanceMeasure} trackingData.registerSelection
   *               Timings for the register selection.
   *
   *        @param {PerformanceMeasure} trackingData.searchIndexesInitialised
   *               Timings for the initialisation of search indexes.
   *
   *        @param {PerformanceMeasure} trackingData.webRegisterAvailable
   *               Time until Web Register was in a useable state.
   */
  public trackInitMetrics({
    dataStoresSynced,
    registerSelection,
    searchIndexesInitialised,
    webRegisterAvailable,
  }: {
    dataStoresSynced: PerformanceMeasure
    registerSelection: PerformanceMeasure
    searchIndexesInitialised: PerformanceMeasure
    webRegisterAvailable: PerformanceMeasure
  }) {
    if (!this.vdPerformanceTracking.canUsePerformanceTracking) {
      return
    }
    this.vdTracking.trackUserEvent({
      eventName: 'Init Metrics',
      metadata: {
        // Sync Type
        isInitialSync: !this.isWarmBoot,
        // Boot Type
        isWarmBoot: this.isWarmBoot,
        // Api Requests
        requests: this._getApiRequests() || 'unknown',
        // Network Details
        network: getNetworkInfo() || 'unknown',
        ...(dataStoresSynced && {
          dataStoresSynced: dataStoresSynced.toJSON(),
        }),
        // Register Selection
        ...(registerSelection && {
          registerSelection: {
            ...registerSelection.toJSON(),
            selectRegisterModalShown: this._selectRegisterModalShown,
          },
        }),
        // Search Indexes
        ...(searchIndexesInitialised && {
          searchIndexesInitialised: searchIndexesInitialised.toJSON(),
        }),
        // Availability
        ...(webRegisterAvailable && {
          webRegisterAvailable: webRegisterAvailable.toJSON(),
        }),
      },
    })
  }

  /**
   * Counts all the API requests that have occurred and identifies the slowest.
   *
   * @method _getApiRequests
   *
   * @return {Object} The count of all API requests and the pathname and duration of the slowest.
   */
  private _getApiRequests() {
    const resources = this.vdPerformanceTracking.getResourceEntries()
    if (!resources || !resources.length) {
      return
    }
    const apiRequests = resources.filter(
      (resource: $TSFixMe) =>
        resource.name.includes('/api/') || resource.name.includes('/pos/status')
    )
    if (!apiRequests.length) {
      return
    }
    const slowestRequest = apiRequests.reduce(
      (slowest: $TSFixMe, current: $TSFixMe) => {
        if (slowest.duration > current.duration) {
          return slowest
        } else {
          return current
        }
      }
    )
    return {
      count: apiRequests.length,
      slowest: {
        url: new URL(slowestRequest.name).pathname,
        duration: slowestRequest.duration,
      },
    }
  }

  /**
   * Perform the initial data sync between the API and IndexedDB.
   *
   * @private
   * @method _performInitialSync
   *
   * @return {Promise} A promise resolved once the initial data sync has completed
   */
  private _performInitialSync() {
    this.isWarmBoot = Boolean(this.posStateManager.getInitialSyncComplete())
    this.syncManager.sync({ isFirstSync: true })
    return this.syncManager.whenCompleteDatasetAvailable()
  }

  /**
   * Wait for a register to be selected. This can be either automatically because a register was already selected
   * in a previous session or there is only one outlet/register, or manually by prompting the user to choose an
   * outlet and register.
   *
   * @private
   * @method _waitForSelectRegister
   *
   * @return {Promise} A promise resolved once a register has been selected
   */
  private _waitForSelectRegister() {
    const activeRegister = this.registerManager.getActiveRegister()
    const resolveSelectedRegister = (selectedRegister: $TSFixMe) => {
      this._selectingRegister = false
      this.emit('registerSelected', { selectedRegister })
      return $q.resolve(selectedRegister)
    }
    if (activeRegister && this._canLoggedInUserAccessActiveOutlet()) {
      return resolveSelectedRegister(activeRegister)
    }
    this._selectingRegister = true
    return this._getAllRegistersByOutlet()
      .then((registersByOutlet: $TSFixMe) => {
        if (registersByOutlet && registersByOutlet.length) {
          this._selectRegisterModalShown = true
          return this._selectRegister(registersByOutlet)
        } else {
          this.emit('registersUnavailable')
          return $q.reject(new RegistersUnavailableError())
        }
      })
      .then(resolveSelectedRegister)
      .catch(error => {
        if (!(error instanceof RegistersUnavailableError)) {
          trackError('Error occurred during register selection', {
            data: { error },
          })
        }
        return $q.reject(error)
      })
  }

  /**
   * Determine whether the current logged in user can access the current active outlet.
   *
   * @private
   * @method _canLoggedInUserAccessActiveOutlet
   *
   * @return {Boolean} true if the current user has access to the current outlet
   */
  private _canLoggedInUserAccessActiveOutlet() {
    const user = this.userManager.getLoggedInUser()
    const outletId = this.posStateManager.getOutletId()!
    return this.userManager.canUserAccessOutlet(user, outletId)
  }

  /**
   * Get the collection of all registers mapped by their respective outlets.
   *
   * @private
   * @method _getAllRegistersByOutlet
   *
   * @return {Promise<Array>} A promise resolved with the collection of registers by outlet
   */
  private _getAllRegistersByOutlet() {
    const activeUser = this.userManager.getLoggedInUser()
    return $q
      .all([
        this.syncManager.whenCompleteEntityDataAvailable('outlets'),
        this.syncManager.whenCompleteEntityDataAvailable('registers'),
      ])
      .then(() =>
        this.registerManager.getAllRegistersByOutlet({
          restrictedOutlets: activeUser.restricted_outlet_ids!,
        })
      )
  }

  /**
   * Select a register either automatically or trigger an event to prompt the UI to display the register select
   * screen to the user.
   *
   * @private
   * @method _selectRegister
   *
   * @param  registersByOutlet {Array}
   *         Collection of all registers mapped by their respective outlets
   *
   * @return {Promise} A promise resolved once a register has been selected
   */
  private _selectRegister(registersByOutlet: $TSFixMe[]) {
    return $q(resolve => {
      // If we only have one register, automatically select it
      if (
        registersByOutlet.length === 1 &&
        registersByOutlet[0].registers.length === 1
      ) {
        const onlyRegisterAndOutlet = registersByOutlet[0]
        const onlyRegister = onlyRegisterAndOutlet.registers[0]
        this.registerManager.setActiveRegister(
          onlyRegister,
          onlyRegisterAndOutlet.outlet
        )
        resolve(onlyRegister)
      } else {
        this.registerManager.once('activeRegisterChange', (event: $TSFixMe) =>
          resolve(event.activeRegister)
        )
        this.emit('registerSelectRequired', { registersByOutlet })
      }
    })
  }

  /**
   * Handle a `loggedInUserChange` event by triggering the register select screen if necessary.
   *
   * @private
   * @method _onLoggedInUserChange
   */
  private _onLoggedInUserChange() {
    // Ensures new user is permitted to access current outlet
    if (!this._selectingRegister) {
      this._waitForSelectRegister()
    }
  }
}

/**
 * Manages the intialisation flow and some of the overall state of the Web Register app. Works in tandem with the
 * WebRegisterCtrl controller.
 *
 * @module webRegisterManager
 */
angular
  .module('webregister.managers')
  .service('webRegisterManager', WebRegisterManager)
